import { Loading } from "@/components/ui/misc";
import { q } from "@/lib/api";
import { authDebug, useAuth } from "@/lib/auth";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";

/**
 * The signed-in frame. There is no side navigation any more (removed 2026-09-23):
 * the studio opens straight on My Demos, and Settings / prospect pages carry their
 * own back links.
 */
/** "Signing you in" that admits it when it's slow, with a retry instead of a silent hang. */
let mounts = 0;
function SlowSignIn({ onRetry, state }: { onRetry: () => void; state: string }) {
  const [slow, setSlow] = useState(false);
  // Diagnostic: how long this instance has waited and how many times it mounted
  // (a climbing count means the frame keeps reloading; a climbing timer means the
  // request itself hangs).
  const [seconds, setSeconds] = useState(0);
  const [mount] = useState(() => ++mounts);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 8000);
    const tick = setInterval(() => setSeconds((n) => n + 1), 1000);
    return () => {
      clearTimeout(t);
      clearInterval(tick);
    };
  }, []);
  const trace = (
    <pre className="max-w-[640px] whitespace-pre-wrap rounded-md bg-muted p-2 text-left text-[11px] text-muted-foreground">
      {[`me: ${state}`, ...authDebug()].join("\n")}
    </pre>
  );
  if (!slow)
    return (
      <div className="p-4">
        <Loading label={`Signing you in (${seconds}s, #${mount})`} />
        {trace}
      </div>
    );
  return (
    <div className="flex flex-col items-center gap-3 p-10 text-center text-sm text-muted-foreground">
      <Loading label="Still signing you in" />
      {trace}
      <div>This is taking longer than it should. The studio API may be restarting.</div>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
      >
        Try again
      </button>
    </div>
  );
}

export function AppShell() {
  const { token, ready, hosted, rejectToken } = useAuth();
  const me = useQuery({
    // Keyed on the token: a fresh token from the host must not reuse a 401 result.
    queryKey: ["me", token],
    queryFn: q.me,
    enabled: Boolean(token),
    retry: (n, err) => n < 1 && (err as { status?: number }).status !== 401,
    // The host renews the token every few minutes (and whenever the tab becomes
    // visible). Keep the previous answer while the new token is checked, or the
    // whole app below (the New demo wizard included) unmounts and loses its state.
    placeholderData: keepPreviousData,
  });
  // The host can hand this tab a token for a DIFFERENT account than the one the
  // cached queries were fetched as (a super-admin leaving an org they browsed as
  // its admin; a stale token replaced a moment later). The dashboard showed the
  // previous account's list under the new name. When the agent id changes, every
  // cached query except `me` is thrown away and refetched under the new identity.
  const qc = useQueryClient();
  const agentId = me.data?.agent.id;
  const seenAgent = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!agentId) return;
    if (seenAgent.current && seenAgent.current !== agentId) {
      void qc.resetQueries({ predicate: (query) => query.queryKey[0] !== "me" });
    }
    seenAgent.current = agentId;
  }, [agentId, qc]);
  // A rejected token is dropped once (then the host is asked for a new one);
  // without this the sign-in page bounced the same bad token straight back here.
  const rejected = useRef<string | null>(null);
  const unauthorized = me.isError && (me.error as { status?: number }).status === 401;
  useEffect(() => {
    if (unauthorized && token && rejected.current !== token) {
      rejected.current = token;
      rejectToken();
    }
  }, [unauthorized, token, rejectToken]);

  if (!ready) return <Loading />;
  // Only inside MyManager (or a dev build's local sign-in). A bare tab is sent to
  // the "open it from MyManager" page even if it somehow holds a token.
  if (!hosted && !(import.meta.env.DEV && token)) return <Navigate to="/signin" replace />;
  if (!token) return <Navigate to="/signin" replace />;
  if (unauthorized) return <Loading label="Refreshing your session" />;
  if (me.isLoading && !me.data)
    return (
      <SlowSignIn
        onRetry={() => me.refetch()}
        state={`${me.status}/${me.fetchStatus} · token len ${token.length} · failures ${me.failureCount}${me.failureReason ? ` — ${(me.failureReason as Error).message}` : ""}`}
      />
    );
  if (me.isError || !me.data) return <Navigate to="/signin" replace />;

  const { capabilities } = me.data;
  const localMode = capabilities.provisioner === "mock" || capabilities.broker === "mock";

  return (
    <div className="min-h-screen">
      {localMode ? (
        <div className="bg-warning/20 px-6 py-1 text-center text-[11px] text-warning">
          Local mode: {capabilities.provisioner === "mock" ? "no demo orgs" : ""}
          {capabilities.provisioner === "mock" && capabilities.broker === "mock" ? ", " : ""}
          {capabilities.broker === "mock" ? "no devices" : ""}
        </div>
      ) : null}
      <main className="min-w-0">
        <Outlet context={me.data} />
      </main>
    </div>
  );
}
